// ===============================================================
// file: Leads.js
// ===============================================================
// This version fixes the Firestore paths so that “leads” are read
// and written from the root-level “contacts” collection (same as
// the Contacts component).  All CRUD operations now point to
// db.collection("contacts") instead of nested personal / team
// paths.  Team switching UI is kept, but currently acts only as
// a visual filter – all data lives in the shared collection.
// ===============================================================

import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../AuthProvider";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  deleteDoc,
  addDoc,
  setDoc,
} from "firebase/firestore";
import {
  FaSearch,
  FaEdit,
  FaTrash,
  FaPlus,
  FaFileImport,
  FaFileExport,
  FaUsers,
  FaCaretDown,
} from "react-icons/fa";
import { toast } from "react-toastify";
import AddLeadModal from "./components/AddLeadModal";
import ImportLeadsModal from "./components/ImportLeadsModal";

// Helper – format large numbers
const formatNumber = (n) => {
  if (n == null) return "";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

// Email sub-tabs keys
const EMAIL_TABS = [
  { key: "day1-problem-solution", label: "D1" },
  { key: "day4-case-study", label: "D4" },
  { key: "day7-role-value", label: "D7" },
  { key: "day10-urgency", label: "D10" },
  { key: "day14-breakup", label: "D14" },
];

// Root collection to use for all leads / contacts
const ROOT_COLL = "contacts";

// =============================================================
// MAIN COMPONENT
// =============================================================
const Leads = () => {
  const { user } = useContext(AuthContext);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Filters
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [activeTab, setActiveTab] = useState("contact");
  const [emailTab, setEmailTab] = useState(EMAIL_TABS[0].key);

  // Team UI state (still present but not used for Firestore paths)
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [personalView, setPersonalView] = useState(true);

  // Fetch leads on mount or when personal/team toggles change
  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Build filters when inputs change
  useEffect(() => {
    let result = [...leads];
    const term = searchTerm.toLowerCase();
    if (term) {
      result = result.filter(
        (l) =>
          l.fullName?.toLowerCase().includes(term) ||
          l.email?.toLowerCase().includes(term) ||
          l.companyName?.toLowerCase().includes(term) ||
          l.title?.toLowerCase().includes(term) ||
          l.industry?.toLowerCase().includes(term)
      );
    }
    if (filterCompany)
      result = result.filter((l) => l.companyName === filterCompany);
    if (filterRole) result = result.filter((l) => l.title === filterRole);
    setFilteredLeads(result);
  }, [leads, searchTerm, filterCompany, filterRole]);

  // Unique company / role lists
  const companies = Array.from(
    new Set(leads.map((l) => l.companyName).filter(Boolean))
  ).sort();
  const roles = Array.from(
    new Set(leads.map((l) => l.title).filter(Boolean))
  ).sort();

  // Pagination helpers
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const paginatedLeads = filteredLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const goToPage = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };
  const changeItemsPerPage = (val) => {
    setItemsPerPage(Number(val));
    setCurrentPage(1);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm("");
    setFilterCompany("");
    setFilterRole("");
    setCurrentPage(1);
  };

  // Open view modal
  const openLeadView = (lead) => {
    setSelectedLead(lead);
    setActiveTab("contact");
    setEmailTab(EMAIL_TABS[0].key);
    setShowViewModal(true);
  };

  // Fetch all leads from root-level collection
  const fetchLeads = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const q = query(collection(db, ROOT_COLL), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
      }));
      setLeads(list);
      setFilteredLeads(list);
    } catch (e) {
      console.error("Error fetching leads:", e);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  // Delete a lead
  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await deleteDoc(doc(db, ROOT_COLL, id));
      setLeads((p) => p.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch (e) {
      console.error("Delete error:", e);
      toast.error("Failed to delete lead");
    }
  };

  // Save (add / update) a lead
  const handleSaveLead = async (lead) => {
    if (!user) return;
    try {
      setLoading(true);
      const now = new Date();
      const data = { ...lead, updatedAt: now };
      if (lead.id) {
        await setDoc(doc(db, ROOT_COLL, lead.id), data, { merge: true });
        setLeads((p) =>
          p.map((l) => (l.id === lead.id ? { ...data, id: lead.id } : l))
        );
        toast.success("Lead updated");
      } else {
        data.createdAt = now;
        const ref = await addDoc(collection(db, ROOT_COLL), data);
        setLeads((p) => [{ ...data, id: ref.id }, ...p]);
        toast.success("Lead added");
      }
      setShowAddLeadModal(false);
      setSelectedLead(null);
    } catch (e) {
      console.error("Save error:", e);
      toast.error("Failed to save lead");
    } finally {
      setLoading(false);
    }
  };

  // Import leads CSV save
  const handleImportSave = async (imported) => {
    if (!user) return;
    try {
      setLoading(true);
      const now = new Date();
      const batchAdds = imported.map((l) =>
        addDoc(collection(db, ROOT_COLL), { ...l, createdAt: now })
      );
      await Promise.all(batchAdds);
      toast.success(`Imported ${imported.length} leads`);
      setShowImportModal(false);
      fetchLeads();
    } catch (e) {
      console.error("Import error:", e);
      toast.error("Failed to import leads");
    } finally {
      setLoading(false);
    }
  };

  // Export leads as CSV
  const handleExportLeads = () => {
    const headers = [
      "fullName",
      "email",
      "companyName",
      "title",
      "industry",
      "createdAt",
    ];
    const csv = [
      headers.join(","),
      ...filteredLeads.map((l) =>
        headers
          .map((h) => {
            const v = l[h];
            if (h === "createdAt") return v ? new Date(v).toISOString() : "";
            return v ? `"${v.toString().replace(/"/g, '""')}"` : "";
          })
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Format date helper
  const fmtDate = (ts) =>
    ts ? (ts.toDate ? ts.toDate() : ts).toLocaleDateString() : "—";

  // ===========================================================
  // RENDER
  // ===========================================================
  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex-1 p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Leads Management</h1>
          <button
            onClick={() => setShowAddLeadModal(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
          >
            <FaPlus className="mr-2" /> Add Lead
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="w-full border rounded-md p-2 pl-10 focus:ring-2 focus:ring-purple-500"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="border rounded-md p-2 flex-1"
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
            >
              <option value="">All Companies</option>
              {companies.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <select
              className="border rounded-md p-2 flex-1"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="">All Roles</option>
              {roles.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
            <button
              onClick={clearFilters}
              className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
            >
              Clear
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center"
            >
              <FaFileImport className="mr-2" /> Import
            </button>
            <button
              onClick={handleExportLeads}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center"
            >
              <FaFileExport className="mr-2" /> Export
            </button>
          </div>
        </div>

        {/* Pagination controls */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            <span>Show</span>
            <select
              className="border rounded px-2 py-1"
              value={itemsPerPage}
              onChange={(e) => changeItemsPerPage(e.target.value)}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
            <span>per page</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              className="border px-3 py-1 rounded"
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              Prev
            </button>
            <span>
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              className="border px-3 py-1 rounded"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {["Name", "Company", "Role", "Email", "Created", "Actions"].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-6 text-center">
                    <div className="animate-spin h-6 w-6 border-2 border-purple-600 border-t-transparent mx-auto rounded-full"></div>
                  </td>
                </tr>
              ) : paginatedLeads.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No leads
                  </td>
                </tr>
              ) : (
                paginatedLeads.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{l.fullName}</td>
                    <td className="px-4 py-3">{l.companyName}</td>
                    <td className="px-4 py-3">{l.title}</td>
                    <td className="px-4 py-3">{l.email}</td>
                    <td className="px-4 py-3">{fmtDate(l.createdAt)}</td>
                    <td className="px-4 py-3 space-x-2 text-right">
                      <button
                        onClick={() => openLeadView(l)}
                        className="text-blue-600"
                      >
                        View
                      </button>
                      <button
                        onClick={() =>
                          setSelectedLead(l) || setShowAddLeadModal(true)
                        }
                        className="text-indigo-600"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => deleteLead(l.id)}
                        className="text-red-600"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showAddLeadModal && (
        <AddLeadModal
          lead={selectedLead}
          onClose={() => {
            setShowAddLeadModal(false);
            setSelectedLead(null);
          }}
          onSave={handleSaveLead}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportLeadsModal
          onClose={() => setShowImportModal(false)}
          onImport={handleImportSave}
        />
      )}

      {/* View Modal */}
      {showViewModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex border-b mb-4">
              {["contact", "company", "emails"].map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  className={`px-4 py-2 ${
                    activeTab === t
                      ? "border-b-2 border-purple-600 font-semibold"
                      : "text-gray-600"
                  }`}
                >
                  {t === "contact"
                    ? "Contact Info"
                    : t === "company"
                    ? "Company Info"
                    : "Emails"}
                </button>
              ))}
            </div>
            {activeTab === "contact" && (
              <div className="space-y-2">
                {[
                  ["Name", selectedLead.fullName],
                  ["Email", selectedLead.email],
                  ["Role", selectedLead.title],
                  ["Industry", selectedLead.industry || "—"],
                ].map(([lab, val]) => (
                  <p key={lab}>
                    <span className="font-semibold">{lab}:</span> {val || "—"}
                  </p>
                ))}
                {selectedLead.summary && (
                  <div className="mt-4">
                    <h3 className="font-semibold">Summary</h3>
                    <pre className="whitespace-pre-wrap">
                      {selectedLead.summary}
                    </pre>
                  </div>
                )}
              </div>
            )}
            {activeTab === "company" && (
              <div className="space-y-2">
                {[
                  ["Company", selectedLead.companyName],
                  ["Domain", selectedLead.domain],
                  [
                    "Employees",
                    selectedLead.employeesCount
                      ? formatNumber(selectedLead.employeesCount)
                      : "—",
                  ],
                ].map(([lab, val]) => (
                  <p key={lab}>
                    <span className="font-semibold">{lab}:</span> {val || "—"}
                  </p>
                ))}
                {selectedLead.company_summary && (
                  <div className="mt-4">
                    <h3 className="font-semibold">Company Summary</h3>
                    <pre className="whitespace-pre-wrap">
                      {selectedLead.company_summary}
                    </pre>
                  </div>
                )}
              </div>
            )}
            {activeTab === "emails" && (
              <>
                <div className="flex space-x-2 border-b mb-4">
                  {EMAIL_TABS.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setEmailTab(t.key)}
                      className={`px-3 py-1 rounded-t ${
                        emailTab === t.key
                          ? "bg-purple-600 text-white"
                          : "bg-gray-100"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="bg-gray-50 p-4 rounded overflow-y-auto max-h-[60vh]">
                  <pre className="whitespace-pre-wrap">
                    {selectedLead[emailTab] || "No content"}
                  </pre>
                </div>
              </>
            )}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
